import React from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';

const PrivacyModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-gray-900/80 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-[#63E61E] to-[#52c518] px-8 py-6 flex items-center justify-between border-b-4 border-gray-900">
            <h2 className="text-3xl font-black text-gray-900">AVISO DE PRIVACIDAD</h2>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-gray-900 hover:bg-gray-800 flex items-center justify-center transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="px-8 py-8 overflow-y-auto max-h-[calc(90vh-120px)]">
            <p className="text-sm text-gray-600 mb-8 italic">Última actualización: 28 de enero de 2026</p>

            <div className="space-y-8 text-gray-700">
              <p className="text-lg leading-relaxed">
                En cumplimiento con lo dispuesto por la Ley Federal de Protección de Datos Personales en Posesión de los Particulares, <strong>LUCARS</strong> pone a su disposición el presente Aviso de Privacidad.
              </p>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="w-2 h-8 bg-[#63E61E] mr-3 rounded"></span>
                  1. Responsable del tratamiento de los datos personales
                </h3>
                <p className="mb-4">
                  <strong>LUCARS</strong>, con operaciones en la Península de Yucatán, es responsable del uso y protección de sus datos personales.
                </p>
                <p>
                  Para cualquier asunto relacionado con el tratamiento de sus datos personales o este Aviso de Privacidad, puede contactarnos a través del correo electrónico:<br />
                  <a href="mailto:contacto@lucars.com.mx" className="text-[#63E61E] font-semibold hover:underline">
                    📧 contacto@lucars.com.mx
                  </a>
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="w-2 h-8 bg-[#63E61E] mr-3 rounded"></span>
                  2. Datos personales que se recaban
                </h3>
                <p className="mb-4">
                  Los datos personales que podemos recabar a través de nuestro sitio web incluyen, de manera enunciativa mas no limitativa:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Nombre completo</li>
                  <li>Número telefónico</li>
                  <li>Correo electrónico</li>
                  <li>Información fiscal (cuando sea necesaria para la prestación de servicios)</li>
                  <li>Datos necesarios para la atención de solicitudes, cotizaciones o contratación de servicios</li>
                </ul>
                <p className="mt-4 font-semibold">
                  LUCARS no recaba datos personales sensibles sin el consentimiento expreso del titular.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="w-2 h-8 bg-[#63E61E] mr-3 rounded"></span>
                  3. Finalidades del tratamiento de los datos personales
                </h3>
                <p className="mb-3">Los datos personales recabados serán utilizados para las siguientes <strong>finalidades primarias:</strong></p>
                <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                  <li>Proveer información sobre nuestros servicios</li>
                  <li>Atender solicitudes, dudas o cotizaciones</li>
                  <li>Establecer contacto comercial</li>
                  <li>Cumplir con obligaciones legales y contractuales</li>
                </ul>
                <p className="mb-3">Adicionalmente, sus datos podrán utilizarse para las siguientes <strong>finalidades secundarias:</strong></p>
                <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                  <li>Envío de información promocional o comercial</li>
                  <li>Evaluar la calidad de nuestros servicios</li>
                </ul>
                <p className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                  Si no desea que sus datos personales sean utilizados para las finalidades secundarias, puede solicitarlo enviando un correo a{' '}
                  <a href="mailto:contacto@lucars.com.mx" className="text-[#63E61E] font-semibold hover:underline">
                    contacto@lucars.com.mx
                  </a>
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="w-2 h-8 bg-[#63E61E] mr-3 rounded"></span>
                  4. Transferencia de datos personales
                </h3>
                <p>
                  Sus datos personales no serán compartidos con terceros, salvo en los casos legalmente permitidos o cuando sea necesario para el cumplimiento de obligaciones legales.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="w-2 h-8 bg-[#63E61E] mr-3 rounded"></span>
                  5. Derechos ARCO
                </h3>
                <p className="mb-4">
                  Usted tiene derecho a <strong>Acceder, Rectificar, Cancelar u Oponerse</strong> al tratamiento de sus datos personales (Derechos ARCO).
                </p>
                <p className="mb-3">
                  Para ejercer cualquiera de estos derechos, deberá enviar una solicitud al correo electrónico:<br />
                  <a href="mailto:contacto@lucars.com.mx" className="text-[#63E61E] font-semibold hover:underline">
                    📧 contacto@lucars.com.mx
                  </a>
                </p>
                <p className="mb-2">La solicitud deberá contener:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Nombre completo del titular</li>
                  <li>Medio para comunicar la respuesta</li>
                  <li>Documentos que acrediten su identidad</li>
                </ul>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="w-2 h-8 bg-[#63E61E] mr-3 rounded"></span>
                  6. Uso de cookies y tecnologías similares
                </h3>
                <p>
                  Nuestro sitio web puede utilizar cookies u otras tecnologías para mejorar la experiencia de navegación. El usuario puede deshabilitarlas desde la configuración de su navegador.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="w-2 h-8 bg-[#63E61E] mr-3 rounded"></span>
                  7. Cambios al Aviso de Privacidad
                </h3>
                <p>
                  LUCARS se reserva el derecho de modificar el presente Aviso de Privacidad en cualquier momento. Las modificaciones estarán disponibles en nuestro sitio web.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                  <span className="w-2 h-8 bg-[#63E61E] mr-3 rounded"></span>
                  8. Consentimiento
                </h3>
                <p>
                  Al utilizar este sitio web o proporcionar sus datos personales, usted manifiesta su consentimiento para el tratamiento de sus datos conforme a este Aviso de Privacidad.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 px-8 py-6 border-t border-gray-200">
            <Button
              onClick={onClose}
              className="w-full bg-[#63E61E] hover:bg-[#52c518] text-gray-900 font-bold py-4 rounded-xl transition-all duration-300"
            >
              Cerrar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyModal;
